import React, { useEffect, useState } from "react";
import {
  PencilSquareIcon,
  TrashIcon,
} from "@heroicons/react/24/outline";
import {
  AiFillThunderbolt,
  AiOutlineCheckCircle,
  AiOutlineCloseCircle,
} from "react-icons/ai";

const inputClass =
  "h-11 w-full rounded-lg border border-slate-200 bg-white px-3 text-sm text-slate-700 outline-none transition focus:border-sky-400 focus:ring-2 focus:ring-sky-100";

const formatMoney = (value) => {
  const number = Number(value || 0);
  return `$${number.toLocaleString(undefined, { maximumFractionDigits: 2 })}`;
};

const formatDate = (date) => {
  if (!date) return "N/A";
  const parsedDate = new Date(date);
  if (Number.isNaN(parsedDate.getTime())) return String(date).slice(0, 10);
  return parsedDate.toLocaleDateString(undefined, {
    month: "short",
    day: "numeric",
    year: "numeric",
  });
};

const getDiscountPercent = (basePrice, salePrice) => {
  const base = Number(basePrice || 0);
  const sale = Number(salePrice || 0);
  if (!base || sale >= base) return 0;
  return Math.round(((base - sale) / base) * 100);
};

const statusClass = (enabled) =>
  enabled ? "bg-emerald-50 text-emerald-700 ring-emerald-100" : "bg-slate-100 text-slate-600 ring-slate-200";

const ExistingDynamicPricingRules = ({ dynamicPricingList, handleStartEditPricing, handleDeletePricing }) => {
  const [randomDiscountStates, setRandomDiscountStates] = useState({});
  const [selectedProductId, setSelectedProductId] = useState(null);
  const [feedbackMessage, setFeedbackMessage] = useState("");

  useEffect(() => {
    const initialState = dynamicPricingList.reduce((acc, pricing) => {
      acc[pricing._id] = {
        enabled: pricing.isRandomDiscountEnabled || false,
        percentage: pricing.discountPercentage || pricing.randomDiscountPercent || "",
        minPrice: pricing.minPrice || "",
        frequency: pricing.discountFrequency || "",
      };
      return acc;
    }, {});
    setRandomDiscountStates(initialState);
  }, [dynamicPricingList]);

  const handleToggleRandomDiscount = (productId) => {
    setRandomDiscountStates((prevStates) => ({
      ...prevStates,
      [productId]: {
        ...prevStates[productId],
        enabled: !prevStates[productId]?.enabled,
      },
    }));
    setSelectedProductId(productId);
    setFeedbackMessage("");
  };

  const handleFormFieldChange = (productId, field, value) => {
    setRandomDiscountStates((prevStates) => ({
      ...prevStates,
      [productId]: {
        ...prevStates[productId],
        [field]: value,
      },
    }));
    setFeedbackMessage("");
  };

  const handleSaveDiscount = () => {
    if (!selectedProductId) return;

    const { percentage, minPrice, frequency, enabled } = randomDiscountStates[selectedProductId] || {};
    if (!percentage || !minPrice || !frequency) {
      setFeedbackMessage("Please fill out all random discount fields before saving.");
      return;
    }

    const payload = { percentage, minPrice, frequency, isRandomDiscountEnabled: enabled };
    fetch(`/api/dynamic-pricing/products/${selectedProductId}/discount`, {
      method: "PUT",
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify(payload),
    })
      .then((response) => response.json())
      .then(() => {
        setFeedbackMessage("Random discount settings saved successfully.");
      })
      .catch((error) => {
        setFeedbackMessage("Error applying random discount.");
        console.error("Error:", error);
      });
  };

  const selectedRule = dynamicPricingList.find((pricing) => pricing._id === selectedProductId);

  return (
    <div>
      {feedbackMessage && (
        <div className="mb-5 rounded-lg border border-sky-100 bg-sky-50 px-4 py-3 text-sm font-semibold text-sky-700">
          {feedbackMessage}
        </div>
      )}

      {selectedProductId && (
        <div className="mb-6 rounded-lg border border-slate-200 bg-slate-50 p-4">
          <div className="mb-4 flex flex-col justify-between gap-2 sm:flex-row sm:items-center">
            <div>
              <h3 className="text-base font-bold text-slate-950">Random Discount Settings</h3>
              <p className="text-sm text-slate-500">
                {selectedRule?.productName || "Selected rule"} discount automation controls.
              </p>
            </div>
            <span className={`inline-flex w-fit items-center gap-1 rounded-full px-2.5 py-1 text-xs font-semibold ring-1 ${statusClass(randomDiscountStates[selectedProductId]?.enabled)}`}>
              {randomDiscountStates[selectedProductId]?.enabled ? <AiOutlineCheckCircle /> : <AiOutlineCloseCircle />}
              {randomDiscountStates[selectedProductId]?.enabled ? "Enabled" : "Disabled"}
            </span>
          </div>

          <div className="grid grid-cols-1 gap-4 lg:grid-cols-4">
            <label className="block">
              <span className="text-sm font-semibold text-slate-700">Discount Percentage</span>
              <input
                type="number"
                value={randomDiscountStates[selectedProductId]?.percentage || ""}
                onChange={(event) => handleFormFieldChange(selectedProductId, "percentage", event.target.value)}
                className={`${inputClass} mt-1`}
                placeholder="Percentage"
              />
            </label>

            <label className="block">
              <span className="text-sm font-semibold text-slate-700">Minimum Price</span>
              <input
                type="number"
                value={randomDiscountStates[selectedProductId]?.minPrice || ""}
                onChange={(event) => handleFormFieldChange(selectedProductId, "minPrice", event.target.value)}
                className={`${inputClass} mt-1`}
                placeholder="Minimum price"
              />
            </label>

            <label className="block">
              <span className="text-sm font-semibold text-slate-700">Frequency</span>
              <select
                value={randomDiscountStates[selectedProductId]?.frequency || ""}
                onChange={(event) => handleFormFieldChange(selectedProductId, "frequency", event.target.value)}
                className={`${inputClass} mt-1`}
              >
                <option value="">Select Frequency</option>
                <option value="daily">Daily</option>
                <option value="weekly">Weekly</option>
                <option value="monthly">Monthly</option>
              </select>
            </label>

            <label className="block">
              <span className="text-sm font-semibold text-slate-700">Random Discount</span>
              <select
                value={randomDiscountStates[selectedProductId]?.enabled ? "enabled" : "disabled"}
                onChange={(event) =>
                  handleFormFieldChange(selectedProductId, "enabled", event.target.value === "enabled")
                }
                className={`${inputClass} mt-1`}
              >
                <option value="enabled">Enabled</option>
                <option value="disabled">Disabled</option>
              </select>
            </label>
          </div>

          <div className="mt-4 flex justify-end">
            <button
              onClick={handleSaveDiscount}
              className="rounded-lg bg-sky-600 px-5 py-2.5 text-sm font-semibold text-white shadow-sm transition hover:bg-sky-700"
            >
              Save Discount
            </button>
          </div>
        </div>
      )}

      <div className="overflow-x-auto">
        <table className="w-full min-w-[1180px] text-left text-sm">
          <thead>
            <tr className="border-y border-slate-100 bg-slate-50 text-xs uppercase tracking-wide text-slate-500">
              <th className="px-4 py-3 font-semibold">Product</th>
              <th className="px-4 py-3 font-semibold">Base</th>
              <th className="px-4 py-3 font-semibold">Sale</th>
              <th className="px-4 py-3 font-semibold">Discount</th>
              <th className="px-4 py-3 font-semibold">Condition</th>
              <th className="px-4 py-3 font-semibold">Window</th>
              <th className="px-4 py-3 font-semibold">Min Price</th>
              <th className="px-4 py-3 font-semibold">Random</th>
              <th className="px-4 py-3 font-semibold">Free Months</th>
              <th className="px-4 py-3 font-semibold">Actions</th>
            </tr>
          </thead>
          <tbody className="divide-y divide-slate-100">
            {dynamicPricingList.length === 0 ? (
              <tr>
                <td colSpan={10} className="px-4 py-10 text-center text-sm text-slate-500">
                  No dynamic pricing rules found.
                </td>
              </tr>
            ) : (
              dynamicPricingList.map((pricing) => {
                const randomEnabled = randomDiscountStates[pricing._id]?.enabled;

                return (
                  <tr key={pricing._id} className="text-slate-700 transition hover:bg-slate-50">
                    <td className="px-4 py-4">
                      <div className="flex items-center gap-3">
                        <div className="flex h-10 w-10 items-center justify-center rounded-lg bg-sky-50 text-sky-700 ring-1 ring-sky-100">
                          <AiFillThunderbolt className="text-xl" />
                        </div>
                        <div>
                          <div className="font-semibold text-slate-950">{pricing.productName || "Untitled product"}</div>
                          <p className="mt-1 text-xs text-slate-500">Rule ID: {String(pricing._id).slice(-6)}</p>
                        </div>
                      </div>
                    </td>
                    <td className="px-4 py-4">{formatMoney(pricing.basePrice)}</td>
                    <td className="px-4 py-4 font-semibold text-slate-950">{formatMoney(pricing.salePrice)}</td>
                    <td className="px-4 py-4">
                      <span className="rounded-full bg-emerald-50 px-2.5 py-1 text-xs font-semibold text-emerald-700 ring-1 ring-emerald-100">
                        {getDiscountPercent(pricing.basePrice, pricing.salePrice)}% off
                      </span>
                    </td>
                    <td className="px-4 py-4">{pricing.condition || "N/A"}</td>
                    <td className="px-4 py-4">
                      <div className="text-xs font-semibold text-slate-700">{formatDate(pricing.startDate)}</div>
                      <div className="mt-1 text-xs text-slate-500">to {formatDate(pricing.endDate)}</div>
                    </td>
                    <td className="px-4 py-4">{formatMoney(pricing.minPrice)}</td>
                    <td className="px-4 py-4">
                      <button
                        type="button"
                        onClick={() => handleToggleRandomDiscount(pricing._id)}
                        className={`inline-flex items-center gap-2 rounded-full px-2.5 py-1 text-xs font-semibold ring-1 transition ${statusClass(randomEnabled)}`}
                      >
                        {randomEnabled ? <AiOutlineCheckCircle /> : <AiOutlineCloseCircle />}
                        {randomEnabled ? "Enabled" : "Disabled"}
                      </button>
                    </td>
                    <td className="px-4 py-4">{pricing.freeMonths || "N/A"}</td>
                    <td className="px-4 py-4">
                      <div className="flex items-center gap-2">
                        <button
                          onClick={() => handleStartEditPricing(pricing)}
                          className="inline-flex h-9 w-9 items-center justify-center rounded-lg border border-slate-200 text-slate-600 transition hover:bg-slate-50"
                          aria-label="Edit pricing rule"
                        >
                          <PencilSquareIcon className="h-5 w-5" />
                        </button>
                        <button
                          onClick={() => handleDeletePricing(pricing._id)}
                          className="inline-flex h-9 w-9 items-center justify-center rounded-lg border border-rose-100 bg-rose-50 text-rose-600 transition hover:bg-rose-100"
                          aria-label="Delete pricing rule"
                        >
                          <TrashIcon className="h-5 w-5" />
                        </button>
                      </div>
                    </td>
                  </tr>
                );
              })
            )}
          </tbody>
        </table>
      </div>
    </div>
  );
};

export default ExistingDynamicPricingRules;
